import { constants } from '@config';

import ImagesFactory from '../../../factory/images';

const AppDecorate = require('../../../app/decorate');
const GraphicCreator = require('../creators/abstract/graphic');
const StickerGetter = require('../getters/sticker');

const GraphicSetter = require('./abstract/graphic');
const ShapeSetter = require('./shape');
const TextSetter = require('./text');

module.exports = StickerSetter;

function StickerSetter () {
  this.handledAttributes = _.union([
    'texts',
    'shapes',
    'content',
    'text_content',
    'fill',
    'sticker_id',
    'over_layout_image_url',
    'source'
  ], this.handledAttributes);

  this.supportedSubElements = ['image', 'ellipse', 'line', 'rect', 'text', 'polygon', 'circle', 'ellipse', 'path', 'g'];
}

StickerSetter.prototype = new GraphicSetter();
StickerSetter.prototype.constructor = StickerSetter;

StickerSetter.prototype.updateContent = GraphicCreator.prototype.updateContent;

StickerSetter.prototype.updateSource = function (element, data) {
  const img = $(element).find('image.subelement.layoutBox')[0];

  if (!img) return;
  if (!img.id) img.id = this.canvas.getNextId();

  INKIVE.DesignFacade.set(img.id, data);
};

StickerSetter.prototype.updateTextContent = function (element, data) {
  const textSetter = new TextSetter();
  const $subTexts = $(element).find('.textarea.active');

  textSetter.initialize();
  _.each($subTexts, function (el) {
    textSetter.set(el, {
      content: data.text_content,
      force_redraw: true,
      cursor_position: data.cursor_position
    });
  });
};

StickerSetter.prototype.updateTexts = function (element, data) {
  const textSetter = new TextSetter();
  const subTexts = $(element).find('.textarea');

  textSetter.initialize();
  _.each(data.texts, function (textData, index) {
    textSetter.set(subTexts[index], textData);
  });

  this.trigger('texts', element.getAttribute('id'), {
    texts: data.texts
  });
};

StickerSetter.prototype.updateShapes = function (element, data) {
  const shapeSetter = new ShapeSetter();
  const elements = this.getElements(element);
  const { shapes } = data;

  shapeSetter.initialize();

  Object.entries(shapes).forEach(([index, shape]) => {
    // Android and iOS call the facade slightly differently, which is why we need to handle it like this.
    const element = elements[index] ?? elements.find(element => element.id === index);

    const targets = shape.fill && shape.fill.previousColor
      ? this.getElementsByColorGroup(elements, shape.fill)
      : element
        ? Object.values(this.getElementsWithColor(element))
        : [];

    targets.forEach(target => shapeSetter.set(target, shape));
  });

  this.trigger('shapes', element.getAttribute('id'), { shapes });
};

StickerSetter.prototype.updateFill = function (element, data) {
  const { cmykFill } = constants.cmykAttributes;
  element.removeAttribute(cmykFill);
  const shapeSetter = new ShapeSetter();

  shapeSetter.initialize();

  _.each(this.getElementsWithColor(element), function (elementWithColor) {
    shapeSetter.set(elementWithColor, data);
  }, this);

  this.trigger('fill', element.getAttribute('id'), {
    fill: data.fill
  });
};

StickerSetter.prototype.updateMissingColorGroup = function (element, data) {
  if (!data?.shapes) {
    return [];
  }

  if (!element) return;

  const shapeSetter = new ShapeSetter();
  shapeSetter.initialize();

  let shapes = Object.values(data.shapes);

  const colorGroups = new Array([...shapes].length).fill(null);

  const nonColorGroupShapes = shapes.map((shape, i) => {
    const group = Number(shape.fill.colorGroup);
    if (!isNaN(group) && group < colorGroups.length) {
      colorGroups[group] = shape.fill.color;
    }

    return shape;
  }).filter((shape, i) => {
    return !colorGroups[shape.fill.colorGroup];
  });

  const nonGroupColors = [...new Set(nonColorGroupShapes.map(shape => shape.fill.color))].filter(Boolean);

  const remainingGroupIndex = colorGroups.map((group, index) => !group ? index : null).filter(group => group !== null);
  nonGroupColors.forEach((color, i) => {
    colorGroups[remainingGroupIndex[i]] = color;
  });

  const colorColorGroupMapping = remainingGroupIndex.reduce((mapping, index) => {
    if (colorGroups[index]) {
      mapping[colorGroups[index]] = `${index}`;
    }

    return mapping;
  }, {});

  shapes = shapes.map(shape => {
    if (!shape.fill.colorGroup && shape.fill.color) {
      shape.fill.colorGroup = colorColorGroupMapping[shape.fill.color];
    }
    return shape;
  });

  shapes.forEach((shape) => {
    shapeSetter.updateColorGroup(element?.querySelector(`#${shape.id}`), { colorGroup: shape.fill.colorGroup });
  });

  return shapes;
};

StickerSetter.prototype.updateStickerId = function (element, data) {
  const $selectedElement = $(element);
  const id = $selectedElement.attr('id');

  const bbox = $selectedElement[0].getBBox();
  const size = Math.max(bbox.width, bbox.height);

  const bboxData = {
    size: {
      width: size,
      height: size
    },
    position: {
      x: bbox.x,
      y: bbox.y
    },
    selected: true
  };

  INKIVE.Helpers.api('search/sticker', 'GET', { id: data.sticker_id }, _.bind(_.bind(function (result) {
    this.updateContent(element, result);
  }, this)));
};

StickerSetter.prototype.updateOverLayoutImageUrl = function (element, data) {
  this.editor.canvas.runExtensions('overLayoutImageUrl', data.over_layout_image_url);
};

/**
 *
 * @param {HTMLImageElement} element
 * @param {{width: number | undefined, height: number | undefined}} data
 * @return {{width: number, height: number}}
 */
StickerSetter.prototype.getSize = function (element, data) {
  return {
    width: data?.width ?? Number(element.getAttribute('width')),
    height: data?.height ?? Number(element.getAttribute('height'))
  };
};

/**
 *
 * @param {HTMLImageElement} element
 * @param {{
 *   width: number | undefined,
 *   height: number | undefined,
 *   svg_url: string,
 * }} data
 */
StickerSetter.prototype.onVersionData = function (element, data) {
  element.classList.add('resizing');
  const size = this.getSize(element, data);

  const cancelLoading = (id) => {
    if (id === element.id) this.cancelImageLoading = true;
    if (element) {
      element.classList.remove('resizing');
    }
  };

  this.cancelImageLoading = false;
  this.trigger('optimize_resolution', element.getAttribute('id'));
  INKIVE.DesignFacade.on('delete', cancelLoading);

  /**
   *
   * @param {number} status
   * @param {HTMLImageElement} optimizedImage
   */
  const smartLoadCallBack = (status, optimizedImage) => {
    INKIVE.DesignFacade.off('delete', cancelLoading);

    const canUpdateImage = status === INKIVE.C.Status.success || data.svg_url;
    data.svg_url = status === INKIVE.C.Status.success
      ? optimizedImage.src
      : data.svg_url;

    element.classList.remove('resizing');
    delete element.dataset.hash;

    if (canUpdateImage && !this.cancelImageLoading) {
      const secureUrl = INKIVE.Helpers.getCORSSecureUrl(data.svg_url);

      svgedit.utilities.assignAttributes(element, { 'xlink:href': secureUrl });
    }
  };

  ImagesFactory.smartLoad(
    data.svg_url,
    size,
    smartLoadCallBack,
    true
  );
};

/**
 *
 * @param {HTMLElement} element
 * @param {{
 * svg_url: string,
 * width: number,
 * height: number}} data
 */
StickerSetter.prototype.resizeExternalSticker = function (element, data) {
  const neededSize = 'tab';
  const params = {
    book_id: INKIVE.C.BOOK.id,
    photo_url: data.svg_url,
    platform: neededSize,
    signed_url: true
  };

  const url = INKIVE.C.BASE_URL + 'inkive/resizeexternalsearchphoto';
  const doPost = _.bind(function () {
    $.api(url, 'POST', params, _.bind(function (result) {
      if (result.STATUS === 'success' && !!result.MESSAGE) {
        const imageData = {
          thumb: result.MESSAGE.replace(`/${neededSize}/`, '/thumb/'),
          mobile: result.MESSAGE.replace(`/${neededSize}/`, '/mobile/'),
          tab: result.MESSAGE.replace(`/${neededSize}/`, '/tab/'),
          web: result.MESSAGE.replace(`/${neededSize}/`, '/web/'),
          largeweb: result.MESSAGE.replace(`/${neededSize}/`, '/largeweb/'),
          original: result.MESSAGE.replace(`/${neededSize}/`, '/original/')
        };

        data.svg_url = result.MESSAGE;
        ImagesFactory.addImageData(imageData, ImagesFactory.defaultAmazonDataNames);
        this.onVersionData(element, data);
      } else {
        this.canvas.call('advice.show', 'image-processing-error');
      }
    }, this));
  }, this);

  doPost();
};

StickerSetter.prototype.getElements = StickerGetter.prototype.getElements;
StickerSetter.prototype.getElementsWithColor = StickerGetter.prototype.getElementsWithColor;
StickerSetter.prototype.defineColorGroups = GraphicSetter.prototype.defineColorGroups;
StickerSetter.prototype.getElementsByColorGroup = StickerGetter.prototype.getElementsByColorGroup;
StickerSetter.prototype = AppDecorate(require('./app/sticker'), StickerSetter.prototype);
